"use client";

import { useCallback, useRef, useState } from "react";

import { AssetImage } from "@/components/optilase/asset-image";
import { CarouselDots } from "@/components/optilase/carousel-dots";
import { activeSlide } from "@/components/optilase/carousel-page";
import { TextLink } from "@/components/optilase/text-link";
import { onSurface, type Surface } from "@/components/optilase/surface";
import { useScrollNeedsFocus } from "@/lib/use-scrollable";
import { cn } from "@/lib/utils";
import { headingLines } from "@/lib/heading-lines";

export type Award = { image: string; title: string; alt?: string };

export type AwardsViewProps = {
  heading: string;
  awards: Award[];
  linkLabel?: string;
  linkHref?: string;
  surface?: Surface | null;
};

export default function AwardsView({
  heading,
  awards,
  linkLabel,
  linkHref = "#",
  surface,
}: AwardsViewProps) {
  const scroller = useRef<HTMLUListElement>(null);
  const isScrollable = useScrollNeedsFocus(scroller);
  const [page, setPage] = useState(0);

  const sync = useCallback(() => {
    const el = scroller.current;
    if (el) setPage(activeSlide(el));
  }, []);

  return (
    <section
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 md:py-12 lg:px-20 lg:py-20", surface ? "" : "bg-teal-02")}
    >
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-11 lg:gap-14">
        <h2
          className={cn(
            "text-section max-w-177.75 text-center",
            onSurface(surface, "text-white", "text-navy-02", "text-navy-02"),
          )}
        >
          {headingLines(heading)}
        </h2>

        <ul
          ref={scroller}
          onScroll={sync}
          tabIndex={isScrollable ? 0 : -1}
          aria-label={isScrollable ? heading : undefined}
          className="-mx-5 flex w-[calc(100%+2.5rem)] snap-x snap-mandatory scroll-pl-5 [scrollbar-width:none] gap-10 overflow-x-auto px-5 md:mx-0 md:w-full md:snap-none md:justify-between md:overflow-visible md:px-0 lg:justify-center lg:gap-40 [&::-webkit-scrollbar]:hidden"
        >
          {awards.map((a) => (
            <li
              key={a.title}
              className="flex w-40 shrink-0 snap-start flex-col items-center justify-start gap-4 text-center md:w-[200px] lg:w-60 lg:gap-5"
            >
              <AssetImage
                src={a.image}
                alt={a.alt || a.title}
                aria-hidden
                width={105}
                height={105}
                className="size-[105px] rounded-xl object-contain"
              />
              <p
                className={cn(
                  "text-sm leading-snug font-medium tracking-[-0.01em] lg:text-xl",
                  onSurface(surface, "text-white/85", "text-grey-08", "text-grey-08"),
                )}
              >
                {a.title}
              </p>
            </li>
          ))}
        </ul>

        <CarouselDots
          count={awards.length}
          active={page}
          label="award"
          onSelect={(i) => {
            const card = scroller.current?.children[i] as HTMLElement | undefined;
            card?.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
          }}
          className="-mt-7 md:hidden"
        />

        {linkLabel ? (
          <>
            <hr
              className={cn(
                "w-full border-t",
                onSurface(surface, "border-white/20", "border-navy-02/15", "border-navy-02/15"),
              )}
            />
            <TextLink
              href={linkHref}
              className={onSurface(surface, "text-white", "text-navy-02", "text-navy-02")}
            >
              {linkLabel}
            </TextLink>
          </>
        ) : null}
      </div>
    </section>
  );
}
